import React, { useEffect } from 'react';
import { FormControl, Button, Grid, Paper, Typography, OutlinedInput, Box, FormLabel, FormHelperText, MenuItem, Select, IconButton, Divider, Checkbox, FormControlLabel, Switch } from '@mui/material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { postLabTestPanelRequest, putLabTestPanelRequest, resetPostLabTestPanel, resetPutLabTestPanel } from '../../../../../redux/modules/laboratory/labTestPanel/slice';
import { getLabDepartmentRequest } from '../../../../../redux/modules/laboratory/labDepartment/slice';
import { getLabTestRequest } from '../../../../../redux/modules/laboratory/labTest/slice';

const validationSchema = yup.object().shape({
    panelCode: yup.string().required("Panel code is required"),
    name: yup.string().required("Name is required"),
    departmentId: yup.string().nullable(),
    price: yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
    tests: yup.array().of(
        yup.object().shape({
            testId: yup.string().required("Test is required"),
        })
    ).min(1, "At least one test is required"),
});

const Form = ({ itemData, toggleList }) => {
    const dispatch = useDispatch();
    let { loading, postRes, putRes } = useSelector(state => state.labTestPanel);
    const departments = useSelector(state => state.labDepartment.getRes?.data);
    const tests = useSelector(state => state.labTest.getRes?.data);

    const { register, handleSubmit, control, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            ...itemData,
            departmentId: itemData?.departmentId?._id || itemData?.departmentId || '',
            isOrderable: itemData?.isOrderable !== false,
            tests: itemData?.tests?.length > 0
                ? itemData.tests.map(t => ({ testId: t.testId?._id || t.testId, sortOrder: t.sortOrder || 0, isOptional: t.isOptional || false }))
                : [{ testId: '', sortOrder: 0, isOptional: false }],
        }
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'tests' });

    useEffect(() => {
        dispatch(getLabDepartmentRequest({ page: 1, limit: 100, searchQuery: '' }));
        dispatch(getLabTestRequest({ page: 1, limit: 500, searchQuery: '' }));
    }, [dispatch]);

    useEffect(() => {
        if (postRes) {
            if (postRes?.success) { toggleList(); }
            toast(postRes?.message);
            dispatch(resetPostLabTestPanel());
        }
        if (putRes) {
            if (putRes?.success) { toggleList(); }
            toast(putRes?.message);
            dispatch(resetPutLabTestPanel());
        }
    }, [postRes, putRes, toggleList, dispatch]);

    const onSubmit = (data) => {
        if (itemData?._id) {
            dispatch(putLabTestPanelRequest(data));
        } else {
            dispatch(postLabTestPanelRequest(data));
        }
    };

    return (
        <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
            <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                <Typography variant="h5" className='title'>Test Panel Form</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Panel Code</FormLabel>
                            <OutlinedInput className="inputfield" {...register('panelCode')} size='small' placeholder="e.g. CBC" />
                            {errors?.panelCode?.message && <FormHelperText error>{errors?.panelCode?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Panel Name</FormLabel>
                            <OutlinedInput className="inputfield" {...register('name')} size='small' placeholder="e.g. Complete Blood Count" />
                            {errors?.name?.message && <FormHelperText error>{errors?.name?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Department</FormLabel>
                            <Controller
                                name="departmentId"
                                control={control}
                                render={({ field }) => (
                                    <Select {...field} size="small" displayEmpty>
                                        <MenuItem value="">Select department</MenuItem>
                                        {departments?.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Panel Price (GYD)</FormLabel>
                            <OutlinedInput className="inputfield" {...register('price')} size='small' type="number" placeholder="0" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>LOINC Code</FormLabel>
                            <OutlinedInput className="inputfield" {...register('loincCode')} size='small' placeholder="Optional" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Orderable</FormLabel>
                            <Controller
                                name="isOrderable"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                                        label={field.value ? 'Yes — appears in the order menu' : 'No — hidden from ordering'}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Panel Tests */}
            <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" className='title' sx={{ mb: 0 }}>Panel Tests</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => append({ testId: '', sortOrder: fields.length, isOptional: false })}>
                        Add Test
                    </Button>
                </Box>
                {errors?.tests?.message && <FormHelperText error sx={{ mb: 2 }}>{errors?.tests?.message}</FormHelperText>}
                {fields.map((field, index) => (
                    <Box key={field.id}>
                        {index > 0 && <Divider sx={{ my: 2 }} />}
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={5}>
                                <FormControl fullWidth>
                                    <FormLabel>Test</FormLabel>
                                    <Controller
                                        name={`tests.${index}.testId`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select {...field} size="small" displayEmpty>
                                                <MenuItem value="">Select test</MenuItem>
                                                {tests?.map(t => <MenuItem key={t._id} value={t._id}>{t.testCode} - {t.name}</MenuItem>)}
                                            </Select>
                                        )}
                                    />
                                    {errors?.tests?.[index]?.testId?.message && <FormHelperText error>{errors.tests[index].testId.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <FormControl fullWidth>
                                    <FormLabel>Sort Order</FormLabel>
                                    <OutlinedInput className="inputfield" {...register(`tests.${index}.sortOrder`)} size='small' type="number" />
                                </FormControl>
                            </Grid>
                            <Grid item xs={4} sm={3}>
                                <Controller
                                    name={`tests.${index}.isOptional`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={<Checkbox checked={field.value} onChange={field.onChange} />}
                                            label="Optional"
                                            sx={{ mt: 2 }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={2} sm={1}>
                                {fields.length > 1 && (
                                    <IconButton color="error" onClick={() => remove(index)} sx={{ mt: 2 }}>
                                        <RemoveCircleOutlineIcon />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Paper>

            <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mt: 3, justifyContent: { xs: 'center', sm: 'right' } }}>
                <Button className="commonBtn" type='submit' variant='contained' disabled={loading}>{itemData?._id ? "Update" : "Save"}</Button>
                <Button variant='outlined' className='btn cancelBtn' onClick={() => { toggleList() }}>Cancel</Button>
            </Box>
        </Box>
    );
};

export default Form;
